import type { CallCategory, CallCategoryCues, MessageSuggestion } from "@expert/shared-types";

import { getAppConfigSingleton } from "@expert/app-config";
import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { useEffect, useRef } from "react";

import { getRootDispatcher } from "@/analytics";

import { useCallCategoryStore } from "./callCategoryStore";
import { emitPacingTargetUpdated } from "./pacingAnalytics";

export function useCallCategoryListener(sessionId: string) {
    const callCategory = useCallCategoryStore((s) => s.callCategory);
    const callStartTimestamp = useRef<number | null>(null);

    // Hydrate persisted category on mount / session change
    useEffect(() => {
        useCallCategoryStore.getState().hydrate(sessionId);
    }, [sessionId]);

    useEffect(() => {
        callStartTimestamp.current = Date.now();

        const subscription = gaiaWsEventBus.on("gaia_ws_message-suggestion", ({ body }: GaiaWebSocketResponse) => {
            const message = body as MessageSuggestion;
            const categoryCue = message.suggestion.cues?.find(
                (cue): cue is CallCategoryCues => cue.type === "CALL_CATEGORY",
            );

            if (categoryCue) {
                void getRootDispatcher().dispatchBusinessEvent("CustomerCueDetected", {
                    detectionSource: "GAIA",
                    cueType: categoryCue.type,
                    cueName: categoryCue.name,
                });

                const previousCategory = useCallCategoryStore.getState().callCategory;
                useCallCategoryStore.getState().setCallCategory(categoryCue.name);

                // Emit PacingTargetUpdated if this is a reclassification
                if (previousCategory && previousCategory !== categoryCue.name) {
                    emitReclassificationEvent(
                        previousCategory,
                        categoryCue.name,
                        message.partner,
                        callStartTimestamp.current,
                    );
                }
            }
        });
        return () => {
            subscription();
            callStartTimestamp.current = null;
            useCallCategoryStore.getState().reset();
        };
    }, [sessionId]);

    return callCategory;
}

function emitReclassificationEvent(
    previousCategory: CallCategory,
    newCategory: CallCategory,
    partner: string,
    callStartTimestamp: number | null,
) {
    const partnerConfig = getAppConfigSingleton().getPartnersConfig(partner);
    const pacingConfig = partnerConfig.pacingTargets;
    if (!pacingConfig?.enabled) return;

    const previousTarget = pacingConfig.callTypeTargets[previousCategory] ?? 0;
    const newTarget = pacingConfig.callTypeTargets[newCategory] ?? 0;
    const elapsedAtUpdate = callStartTimestamp ? Math.floor((Date.now() - callStartTimestamp) / 1000) : 0;

    emitPacingTargetUpdated({
        previousCallCategory: previousCategory,
        newCallCategory: newCategory,
        previousTargetSeconds: previousTarget,
        newTargetSeconds: newTarget,
        elapsedAtUpdateSeconds: elapsedAtUpdate,
    });
}
